import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  BoundaryEditAction, BoundaryEditManualMethod,
} from '~/boundary/editMode/boundaryEditModal/boundaryEditModal.helpers';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { useBoundaryEditAnother } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/useBoundaryEditAnother';
import type { BoundaryCombineGeometryRequestItem } from '~/store/boundaries/boundaries.repository';
import {
  boundarySelectEditCloseModal, boundarySelectEditDeactivate,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import {
  type BoundarySelectState,
  isBoundarySelectEditCustomBoundary,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';

export const useBoundarySelectEditCustomBoundary = () => {
  const dispatch = useDispatch();
  const { openModal: openBoundaryCreateSubmitModal } = useModal(ModalType.BoundaryCreateSubmit);
  const { openModal: openBoundaryEditSubmitModal } = useModal(ModalType.BoundaryEditSubmit);
  const { onBoundaryEditSubmit } = useBoundaryEditAnother();

  const onBoundarySelectEditCustomBoundarySubmit = useCallback((selectState: BoundarySelectState) => {
    if (!isBoundarySelectEditCustomBoundary(selectState)) {
      return;
    }

    const geometry: BoundaryCombineGeometryRequestItem[] = Array.from(selectState.selectedBoundaryIds).map(boundaryId => ({
      type: 'boundary',
      boundary_group_id: selectState.selectBoundaryGroupId,
      boundary_id: boundaryId,
    }));

    if (selectState.selectionModeData.action === BoundaryEditAction.CreateNewBoundary) {
      openBoundaryCreateSubmitModal({
        geometry,
        editedBoundaryTerritoryGroupId: selectState.selectionModeData.boundaryTerritoryGroupId,
        onCreate: (createAnother: boolean) => {
          if (createAnother) {
            dispatch(boundarySelectEditDeactivate());
          }
          else {
            dispatch(boundarySelectEditCloseModal());
          }
        },
      });
    }
    else {
      dispatch(boundarySelectEditCloseModal());

      const isEditingSpecificBoundary = selectState.selectionModeData.boundaryId !== undefined;

      openBoundaryEditSubmitModal({
        geometry,
        editedBoundaryTerritoryGroupId: selectState.selectionModeData.boundaryTerritoryGroupId,
        selectBoundaryGroupId: selectState.selectBoundaryGroupId,
        selectedBoundaryId: selectState.selectionModeData.boundaryId,
        method: BoundaryEditManualMethod.Selection,
        isBoundarySelectDisabled: isEditingSpecificBoundary,
        hideEditAnother: isEditingSpecificBoundary,
        onSubmit: (editAnother) => {
          onBoundaryEditSubmit({
            editAnother,
            editedBoundaryTerritoryGroupId: selectState.selectionModeData.boundaryTerritoryGroupId,
            selectMethodBoundaryGroupId: selectState.selectBoundaryGroupId,
            method: BoundaryEditManualMethod.Selection,
            onEditAnotherSubmit: (selectState: BoundarySelectState) => {
              onBoundarySelectEditCustomBoundarySubmit(selectState);
            },
            onEditAnotherClose: () => {
              dispatch(boundarySelectEditCloseModal());
            },
          });
        },
      });
    }
  }, [dispatch, onBoundaryEditSubmit, openBoundaryCreateSubmitModal, openBoundaryEditSubmitModal]);

  return {
    onBoundarySelectEditCustomBoundarySubmit,
  };
};
